import type { Entity } from '@backstage/catalog-model';
import type { KeyRolesFields } from '@ipen-portal/plugin-key-roles-common';
import type { RelationalKeyRoleListItems } from '@ipen-portal/plugin-key-roles-react';
import type { ComponentProps } from 'react';

import { isGroupEntity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  isBusinessDomainEntity,
  isChapterEntity,
  isOrganisationDsoEntity,
  isOrganisationEntity,
  isProductEntity,
  isSolutionEntity,
  isSquadEntity,
} from '@ipen-portal/ipen-entity-model';
import { DetailTile, InfoButton } from '@ipen-portal/plugin-common-react';
import { KeyRoleList } from '@ipen-portal/plugin-key-roles-react';
import { tss } from 'tss-react/mui';

const useStyles = tss.create({
  root: {
    '& div:last-child': {
      paddingRight: 0,
    },
  },
});

type Props<E extends Entity> = {
  predicate: (e: Entity) => e is E;
  relationalRoles?: Omit<
    ComponentProps<typeof RelationalKeyRoleListItems<E>>,
      'clickable' | 'entity'
  >[];
  rolesToExclude?: KeyRolesFields[];
  rolesToInclude?: KeyRolesFields[];
};

const createConfig = <E extends Entity>(props: Props<E>): Props<E> => props;

const configs = [
  createConfig({
    predicate: isOrganisationEntity,
    rolesToInclude: ['digitalLead'],
  }),
  createConfig({
    predicate: isOrganisationDsoEntity,
    rolesToExclude: ['parent'],
  }),
  createConfig({
    predicate: isSquadEntity,
    rolesToExclude: ['parent'],
  }),
  createConfig({
    predicate: isProductEntity,
    rolesToExclude: ['owner', 'businessOwner'],
  }),
  createConfig({
    predicate: isSolutionEntity,
    relationalRoles: [
      {
        entityPredicate: isSolutionEntity,
        fieldKind: 'Domain',
        referredEntityPredicate: isBusinessDomainEntity,
        relationFieldExtractor: param => param.spec.domain,
        rolesToInclude: ['owner'],
      },
    ],
    rolesToExclude: ['businessOwner'],
  }),
  createConfig({
    predicate: isChapterEntity,
    rolesToInclude: ['chapterLead'],
  }),

  // fallback
  createConfig({ predicate: isGroupEntity }),
] as const;

const KeyRolesCardInternal = <E extends Entity>() => {
  const { classes } = useStyles();
  const { entity } = useEntity();
  if (!entity) {
    return null;
  }
  const props = configs.find(({ predicate }) => predicate(entity));
  if (!props) {
    return null;
  }

  const { relationalRoles, rolesToExclude, rolesToInclude } =
    props as unknown as Props<E>;

  return (
    <DetailTile
      action={
        <InfoButton link="/docs/default/domain/ipen-domain/collaboration/organisation/groups-and-roles/" />
      }
      className={classes.root}
      icon="bi:person-circle"
      title="Key Roles"
    >
      <KeyRoleList
        entity={entity}
        relationalRoles={relationalRoles}
        rolesToExclude={rolesToExclude}
        rolesToInclude={rolesToInclude}
      />
    </DetailTile>
  );
};

export const KeyRolesCard = EntityCardBlueprint.make({
  name: 'key-roles',
  params: {
    filter: entity => configs.some(value => value.predicate(entity)),
    loader: async () => <KeyRolesCardInternal />,
    type: 'content',
  },
});
